import moment from 'moment-timezone';
import { createSelector } from 'reselect';
import get from 'lodash/get';
import some from 'lodash/some';
import permissionConstants from 'xm-constants/permissions';

const { ABILITY_ACT_SUPPORTUSER, ABILITY_ACT_REASSIGN_FLOW_STEP_OWNER } =
  permissionConstants;

const STAKEHOLDER_USER = 'STAKEHOLDER_USER';

export const getLoggedInUser = state => state.loggedInUser;

export const getPermissions = state => {
  return state.loggedInUser.loggedInUserProfile.permissions;
};

export const getTimezone = createSelector([getLoggedInUser], loggedInUser =>
  get(loggedInUser, 'loggedInUserProfile.timezone', 'America/Vancouver'),
);

export const getTimezoneOrGuess = createSelector(
  [getLoggedInUser],
  loggedInUser =>
    get(loggedInUser, 'loggedInUserProfile.timezone', moment.tz.guess()),
);

export const getCustomTimezone = createSelector(
  [getLoggedInUser],
  loggedInUser =>
    get(loggedInUser, 'loggedInUserProfile.customTimezone', 'US/Pacific'),
);

export const getIsCompanyAdmin = createSelector(
  [getLoggedInUser],
  loggedInUser => !!get(loggedInUser, 'isCompanyAdmin'),
);

export const hasPermissionReassignFlowStepOwner = createSelector(
  [getPermissions],
  permissions =>
    some(permissions, {
      id: ABILITY_ACT_REASSIGN_FLOW_STEP_OWNER,
    }),
);

export const getLoggedInUserProfile = createSelector(
  [getLoggedInUser],
  loggedInUser => get(loggedInUser, 'loggedInUserProfile'),
);

export const getLoggedInUserId = createSelector(
  [getLoggedInUserProfile],
  loggedInUserProfile => get(loggedInUserProfile, 'id'),
);

// Because state.loggedInUser.loggedInUserId !== state.loggedInUser.loggedInUserProfile.id
// ie: 'xm-support' !== '4b6fe547-24da-c5eb-cdea-1fa664f8053e'
export const getLoggedInUserIdString = createSelector(
  [getLoggedInUser],
  loggedInUser => get(loggedInUser, 'loggedInUserId'),
);

export const getLoggedInUserEncryptedUserId = createSelector(
  [getLoggedInUser],
  loggedInUser => get(loggedInUser, 'encryptedUserId'),
);

export const getLoggedInUserSite = createSelector(
  [getLoggedInUser],
  loggedInUser => get(loggedInUser, 'loggedInUserProfile.site'),
);

export const getIsCompanySupervisor = createSelector(
  [getLoggedInUserProfile],
  loggedInUserProfile =>
    loggedInUserProfile?.roles?.data?.some(
      d => d.name === 'Company Supervisor',
    ),
);

export const getLoggedInUserRoles = createSelector(
  [getLoggedInUserProfile],
  loggedInUserProfile => loggedInUserProfile?.roles?.data ?? [],
);

export const getHasCompanySupervisorPermission = createSelector(
  [getLoggedInUserProfile],
  loggedInUserProfile =>
    loggedInUserProfile?.permissions.some(
      permission =>
        permission.id === permissionConstants.ABILITY_ACT_COMPANY_SUPERVISOR,
    ),
);

export const getIsAdminOrCompanySupervisor = createSelector(
  [
    getIsCompanyAdmin,
    getIsCompanySupervisor,
    getHasCompanySupervisorPermission,
  ],
  (isCompanyAdmin, isCompanySupervisor, hasCompanySupervisorPermission) =>
    isCompanyAdmin || isCompanySupervisor || hasCompanySupervisorPermission,
);

export const getLoggedInUserLanguage = createSelector(
  [getLoggedInUser],
  loggedInUser => get(loggedInUser, 'loggedInUserProfile.language'),
);

export const getIsSuperAdmin = createSelector(
  [getLoggedInUser],
  loggedInUser => loggedInUser.isSuperAdmin,
);

export const getIsSupportUser = createSelector([getPermissions], permissions =>
  some(permissions, {
    id: ABILITY_ACT_SUPPORTUSER,
  }),
);

export const getIsStakeholderUser = state =>
  getLoggedInUserProfile(state).licenseType === STAKEHOLDER_USER;
